// Cliente HTTP centralizado para a API REST externa de rastreamento.
// Base URL vem de import.meta.env.VITE_API_URL. Sem backend próprio.
import { logActivity } from "@/services/gpsdrive-activity-log.service";
import { recoverTraccarSession } from "@/lib/api/traccar-session-recovery";
import { fetchWithNetworkRetry } from "@/lib/api/fetch-retry";
import { reportWrite } from "@/lib/live-sync";
import { resolveApiBase } from "@/lib/api/base-url";

export const API_URL: string = resolveApiBase(
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_URL) || "",
);

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

// Erros do Traccar (ex.: violação de unique constraint) chegam como corpo
// texto/plain com o stack trace inteiro (ver org.traccar.api.
// ResourceErrorHandler — não JSON), então o generic `${status} ao acessar
// ${path}` não diz nada útil ao usuário. Aqui extraímos o padrão real do
// Postgres ("Key (coluna)=(valor) already exists") quando presente; caso
// contrário cai na mensagem genérica de sempre.
async function describeError(res: Response, path: string): Promise<string> {
  const fallback = `Erro ${res.status} ao acessar ${path}`;
  try {
    const text = await res.text();
    const match = text.match(/Key \(([^)]+)\)=\(([^)]+)\) already exists/);
    if (match) {
      const [, column, value] = match;
      return `Já existe um registro com ${column} = "${value}"`;
    }
  } catch {
    // corpo não pôde ser lido — cai no genérico abaixo
  }
  return fallback;
}

// Notificado sempre que uma chamada real à API retorna 401, para que o
// AuthContext possa limpar a sessão e o AuthGate redirecionar para /login.
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  // Dados mockados retornados quando VITE_API_URL não está configurada.
  fallback?: unknown;
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<T> {
  const { body, fallback, headers, ...rest } = options;

  // Modo demo: sem API configurada, devolve fallback (quando fornecido).
  if (!API_URL) {
    if (fallback === undefined) {
      throw new ApiError("VITE_API_URL não configurada", 0);
    }
    await new Promise((r) => setTimeout(r, 200));
    return fallback as T;
  }

  const res = await fetchWithNetworkRetry(`${API_URL}${path}`, {
    // Sem isso o cookie de sessão do Traccar não vai em NENHUMA chamada que
    // não passe "credentials: include" manualmente — API_URL é cross-origin
    // (api.gpsdrive.com.br vs gpsdrive.com.br), e o modo padrão do fetch
    // ("same-origin") nunca envia cookie em request cross-origin, ponto,
    // independente de SameSite. Isso gerava 401 fantasma (sessão válida,
    // cookie simplesmente não enviado) em toda chamada feita via
    // apiGet/apiPost/apiPut/apiDelete (nenhum desses aceitava esse campo) e
    // em todo apiRequest() direto que esqueceu de passar o campo — cada 401
    // fantasma desses dispara unauthorizedHandler e desloga o usuário na
    // hora, mesmo com sessão perfeitamente válida (bug relatado 2026-08-31:
    // "o sistema fica deslogando toda hora"). Definir o padrão aqui garante
    // que toda chamada envia o cookie, sem depender de cada call site
    // lembrar disso; ainda pode ser sobrescrito passando credentials
    // explicitamente nas opções.
    credentials: "include",
    ...rest,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers ?? {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    if (res.status === 401) {
      // Sessão do Traccar perdida (ex.: Traccar reiniciou) com a do GPS
      // Drive ainda válida: recria e repete a chamada uma vez antes de
      // mandar o usuário pro login.
      if (!isRetry && (await recoverTraccarSession())) {
        return apiRequest<T>(path, options, true);
      }
      unauthorizedHandler?.();
      throw new ApiError(await describeError(res, path), res.status);
    }
    const message = await describeError(res, path);
    // Aba LOGS (Editar Usuário) — só chamadas mutantes (GET falhando é
    // normal demais, ex. polling, pra virar "erro" no histórico do
    // usuário). 401 já tratado acima, não repete aqui.
    if (rest.method && rest.method !== "GET") {
      logActivity({
        type: "ERROR",
        message,
        metadata: { path, method: rest.method, status: res.status },
      });
    }
    throw new ApiError(message, res.status);
  }
  reportWrite("traccar", rest.method, path);
  // 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiGet = <T>(path: string, fallback?: T) =>
  apiRequest<T>(path, { method: "GET", fallback });

export const apiPost = <T>(path: string, body?: unknown, fallback?: T) =>
  apiRequest<T>(path, { method: "POST", body, fallback });

export const apiPut = <T>(path: string, body?: unknown, fallback?: T) =>
  apiRequest<T>(path, { method: "PUT", body, fallback });

export const apiDelete = <T>(path: string, fallback?: T) =>
  apiRequest<T>(path, { method: "DELETE", fallback });
